import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "./ui/Modal";
import { Check, FileText, Loader2, Upload } from "lucide-react";
import { supabase } from "../supabaseClient";
import type { InvoiceTemplate, User } from "../types";

export type InvoiceTemplateModalMode = "devis" | "facture";

interface InvoiceTemplateFormState {
  theme: NonNullable<InvoiceTemplate["theme"]>;
  accentColor: string;
  footerText: string;
  useProfilePhotoAsLogo: boolean;
  logoUrl: string;
  introText: string;
  termsText: string;
  pageOrientation: NonNullable<InvoiceTemplate["pageOrientation"]>;
  fontSize: NonNullable<InvoiceTemplate["fontSize"]>;
  logoPosition: NonNullable<InvoiceTemplate["logoPosition"]>;
}

interface InvoiceTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: InvoiceTemplateModalMode;
  ownerId: string;
  ownerProfile: User | null | undefined;
  ownerProfileLoading: boolean;
  updateOwnerPreferences: (prefs: Record<string, unknown>) => Promise<{ error: unknown }>;
  companyName: string;
}

const defaultForm: InvoiceTemplateFormState = {
  theme: "classic",
  accentColor: "#1d4ed8",
  footerText: "",
  useProfilePhotoAsLogo: false,
  logoUrl: "",
  introText: "",
  termsText: "",
  pageOrientation: "portrait",
  fontSize: "normal",
  logoPosition: "left",
};

function normalizeHex(s: string) {
  const hex = s.trim().replace(/^#/, "");
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) return `#${hex}`;
  if (/^[0-9A-Fa-f]{3}$/.test(hex)) return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  return undefined;
}

export function InvoiceTemplateModal({
  isOpen,
  onClose,
  mode,
  ownerId,
  ownerProfile,
  ownerProfileLoading,
  updateOwnerPreferences,
  companyName,
}: InvoiceTemplateModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<InvoiceTemplateFormState>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDragOver, setLogoDragOver] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen || !ownerProfile?.preferences) return;
    const tpl = ownerProfile.preferences.invoiceTemplate;
    if (tpl) {
      setForm({
        theme: tpl.theme ?? "classic",
        accentColor: tpl.accentColor ?? "#1d4ed8",
        footerText: tpl.footerText ?? "",
        useProfilePhotoAsLogo: tpl.useProfilePhotoAsLogo ?? false,
        logoUrl: tpl.logoUrl ?? "",
        introText: tpl.introText ?? "",
        termsText: tpl.termsText ?? "",
        pageOrientation: tpl.pageOrientation ?? "portrait",
        fontSize: tpl.fontSize ?? "normal",
        logoPosition: tpl.logoPosition ?? "left",
      });
    } else {
      setForm(defaultForm);
    }
  }, [isOpen, ownerProfile?.preferences]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    const accent = normalizeHex(form.accentColor) ?? "#1d4ed8";
    const tpl: InvoiceTemplate = {
      theme: form.theme,
      accentColor: accent,
      footerText: form.footerText.trim() || undefined,
      useProfilePhotoAsLogo: form.useProfilePhotoAsLogo || undefined,
      logoUrl: form.useProfilePhotoAsLogo ? undefined : (form.logoUrl.trim() || undefined),
      introText: form.introText.trim() || undefined,
      termsText: form.termsText.trim() || undefined,
      pageOrientation: form.pageOrientation,
      fontSize: form.fontSize,
      logoPosition: form.logoPosition,
    };
    const prefs = { ...ownerProfile?.preferences, invoiceTemplate: tpl };
    const { error } = await updateOwnerPreferences(prefs);
    setSaving(false);
    if (!error) {
      setSuccess(true);
      setForm((f) => ({ ...f, accentColor: accent }));
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!file?.type.startsWith("image/") || file.size > 2 * 1024 * 1024) return;
    setLogoUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      const filePath = `${ownerId}/invoice-logo.${ext}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(filePath, file, { cacheControl: "3600", upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);
      setForm((f) => ({ ...f, logoUrl: urlData.publicUrl, useProfilePhotoAsLogo: false }));
    } catch {
      // silent
    } finally {
      setLogoUploading(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = "";
    }
  };

  const titleLabel = mode === "devis" ? t("settings.myQuotes") : t("settings.myInvoices");
  const docTitle = mode === "devis" ? "DEVIS" : "FACTURE";

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="premium" size="xl" noPadding>
      <div className="flex h-[78vh] min-h-[420px] max-h-[720px]">
        {/* Colonne gauche : aperçu fixe type document */}
        <aside className="w-[260px] sm:w-[280px] shrink-0 flex flex-col items-center justify-center p-6 bg-gray-50/80 dark:bg-white/[0.02] border-r border-gray-200/60 dark:border-white/[0.06]">
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-3">
            {t("settings.invoiceTemplatePreview")}
          </p>
          <div
            className="rounded-xl overflow-hidden bg-white dark:bg-white/[0.04] border border-gray-200/70 dark:border-white/[0.08] shadow-lg shadow-black/5 dark:shadow-none w-full max-w-[240px] transition-all duration-200"
            style={{
              aspectRatio: form.pageOrientation === "landscape" ? "297/210" : "210/297",
            }}
          >
            <div className="h-full min-h-0 flex flex-col p-3 gap-0">
              <div
                className={`flex items-center gap-1.5 shrink-0 pb-1.5 mb-1.5 border-b ${
                  form.theme === "minimal" ? "border-gray-200/50" : form.theme === "luxe" ? "border-gray-300/70 dark:border-white/10" : "border-gray-200/60 dark:border-white/[0.08]"
                } ${form.logoPosition === "right" ? "flex-row-reverse" : ""}`}
              >
                {((form.useProfilePhotoAsLogo && ownerProfile?.avatar_url) || form.logoUrl) ? (
                  form.useProfilePhotoAsLogo && ownerProfile?.avatar_url ? (
                    <img src={ownerProfile.avatar_url} alt="" className="w-6 h-6 rounded-md object-cover shrink-0 ring-1 ring-black/5" />
                  ) : form.logoUrl ? (
                    <img src={form.logoUrl} alt="" className="w-6 h-6 rounded-md object-contain shrink-0 bg-gray-50 dark:bg-white/5 ring-1 ring-black/5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : null
                ) : null}
                <span className={`font-semibold text-gray-900 dark:text-white truncate min-w-0 ${form.fontSize === "small" ? "text-[8px]" : form.fontSize === "large" ? "text-[11px]" : "text-[9px]"}`}>
                  {companyName || t("settings.invoicePreviewCompany")}
                </span>
              </div>
              <div className="shrink-0 mb-1.5">
                <span
                  className={`font-bold tracking-tight block ${form.fontSize === "small" ? "text-[9px]" : form.fontSize === "large" ? "text-[12px]" : "text-[10px]"}`}
                  style={{ color: /^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : "#1d4ed8" }}
                >
                  {docTitle}
                </span>
                {form.theme === "luxe" && (
                  <div className="w-5 h-0.5 rounded-full mt-0.5" style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : "#1d4ed8" }} />
                )}
              </div>
              {form.introText.trim() ? (
                <p className="shrink-0 text-gray-600 dark:text-slate-400 line-clamp-2 mb-1.5 text-[7px] leading-tight">{form.introText.trim()}</p>
              ) : null}
              <div className={`shrink-0 grid gap-y-0.5 mb-1.5 ${form.fontSize === "small" ? "text-[6px]" : form.fontSize === "large" ? "text-[9px]" : "text-[7px]"}`}>
                <span className="text-gray-500 dark:text-slate-500 uppercase tracking-wider font-medium">Client</span>
                <span className="text-gray-800 dark:text-slate-200 truncate block">Jean Dupont</span>
                <span className="text-gray-500 dark:text-slate-500 uppercase tracking-wider font-medium mt-0.5">Date</span>
                <span className="text-gray-800 dark:text-slate-200 block">12/02/2025</span>
              </div>
              {form.termsText.trim() ? <p className="shrink-0 text-gray-500 dark:text-slate-500 line-clamp-2 mb-1.5 text-[6px] leading-tight">{form.termsText.trim()}</p> : null}
              <div className="flex-1 min-h-1" />
              <div
                className={`shrink-0 flex justify-between items-center pt-1.5 border-t ${form.theme === "minimal" ? "border-gray-200/50" : "border-t-[1.5px]"}`}
                style={{ borderColor: form.theme === "luxe" && /^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : undefined }}
              >
                <span className={`font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wider ${form.fontSize === "small" ? "text-[6px]" : "text-[7px]"}`}>Total TTC</span>
                <span className={`font-bold tabular-nums ${form.fontSize === "small" ? "text-[9px]" : "text-[10px]"}`} style={{ color: /^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : "#1d4ed8" }}>
                  1 234,56 €
                </span>
              </div>
              {form.footerText.trim() ? <p className="shrink-0 text-center text-gray-400 dark:text-slate-500 truncate mt-0.5 text-[6px]">{form.footerText.trim()}</p> : null}
            </div>
          </div>
        </aside>

        {/* Colonne droite : options défilantes */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="p-6 pr-10 pt-10 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight pr-8">
                {titleLabel}
              </h2>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                {t("settings.invoiceTemplateDesc")}
              </p>
            </div>

            {/* Styles partagés : groupes de boutons + champs */}
            <style>{`
              .invoice-segmented { display: inline-flex; padding: 3px; border-radius: 12px; background: rgba(0,0,0,0.04); }
              .dark .invoice-segmented { background: rgba(255,255,255,0.06); }
              .invoice-segmented button { padding: 8px 14px; border-radius: 10px; font-size: 13px; font-weight: 500; transition: all 0.2s; }
              .invoice-segmented button:not(.active) { color: #6b7280; }
              .dark .invoice-segmented button:not(.active) { color: #94a3b8; }
              .invoice-segmented button.active { background: white; color: #111827; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
              .dark .invoice-segmented button.active { background: rgba(255,255,255,0.12); color: white; }
            `}</style>

            {/* 1. Mise en page */}
            <section className="space-y-4">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white tracking-tight">{t("settings.invoiceTemplateLayout")}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplatePageOrientation")}</label>
                  <div className="invoice-segmented">
                    {(["portrait", "landscape"] as const).map((orient) => (
                      <button key={orient} type="button" onClick={() => setForm((f) => ({ ...f, pageOrientation: orient }))} className={form.pageOrientation === orient ? "active" : ""}>
                        {t(`settings.invoiceOrientation_${orient}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateFontSize")}</label>
                  <div className="invoice-segmented">
                    {(["small", "normal", "large"] as const).map((size) => (
                      <button key={size} type="button" onClick={() => setForm((f) => ({ ...f, fontSize: size }))} className={form.fontSize === size ? "active" : ""}>
                        {t(`settings.invoiceFontSize_${size}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Apparence */}
            <section className="space-y-4 pt-6 border-t border-gray-100 dark:border-white/[0.06]">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white tracking-tight">{t("settings.invoiceTemplateAppearance")}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateTheme")}</label>
                  <div className="invoice-segmented">
                    {(["minimal", "classic", "luxe"] as const).map((theme) => (
                      <button key={theme} type="button" onClick={() => setForm((f) => ({ ...f, theme }))} className={form.theme === theme ? "active" : ""}>
                        {t(`settings.invoiceTheme_${theme}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 p-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06]">
                  <label className="text-[13px] font-medium text-gray-900 dark:text-white">{t("settings.invoiceTemplateAccentColor")}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={/^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : "#1d4ed8"} onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))} className="w-9 h-9 rounded-xl border border-gray-200 dark:border-white/10 cursor-pointer bg-transparent overflow-hidden" />
                    <input type="text" value={form.accentColor} onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))} className="w-20 h-9 px-2.5 rounded-xl bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 text-[12px] font-mono text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20" placeholder="#1d4ed8" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateLogoPosition")}</label>
                  <div className="invoice-segmented">
                    {(["left", "right"] as const).map((pos) => (
                      <button key={pos} type="button" onClick={() => setForm((f) => ({ ...f, logoPosition: pos }))} className={form.logoPosition === pos ? "active" : ""}>
                        {t(`settings.invoiceLogoPosition_${pos}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">{t("settings.invoiceTemplateLogo")}</label>
                  <label className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06] cursor-pointer hover:bg-gray-100/80 dark:hover:bg-white/[0.06] transition-colors">
                    <div className="flex items-center gap-3">
                      {ownerProfile?.avatar_url && <img src={ownerProfile.avatar_url} alt="" className="w-9 h-9 rounded-xl object-cover ring-1 ring-black/5" />}
                      <span className="text-[13px] font-medium text-gray-900 dark:text-white">{t("settings.invoiceTemplateUseProfilePhoto")}</span>
                    </div>
                    <input type="checkbox" checked={form.useProfilePhotoAsLogo} onChange={(e) => setForm((f) => ({ ...f, useProfilePhotoAsLogo: e.target.checked, ...(e.target.checked ? { logoUrl: "" } : {}) }))} className="w-4 h-4 rounded border-gray-300 text-[#007AFF] focus:ring-[#007AFF]" />
                  </label>
                  <input ref={logoFileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); }} />
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); logoFileInputRef.current?.click(); } }}
                    onDragOver={(e) => { e.preventDefault(); if (!form.useProfilePhotoAsLogo) setLogoDragOver(true); }}
                    onDragLeave={() => setLogoDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setLogoDragOver(false); if (!form.useProfilePhotoAsLogo && e.dataTransfer.files?.[0]?.type.startsWith("image/")) handleLogoUpload(e.dataTransfer.files[0]); }}
                    onClick={() => !form.useProfilePhotoAsLogo && logoFileInputRef.current?.click()}
                    className={`rounded-xl border-2 border-dashed p-4 text-center text-[13px] text-gray-500 dark:text-slate-400 cursor-pointer transition-all ${logoDragOver && !form.useProfilePhotoAsLogo ? "border-[#007AFF] bg-[#007AFF]/10 dark:bg-[#007AFF]/15" : "border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02] hover:border-gray-300 dark:hover:border-white/15"} ${form.useProfilePhotoAsLogo ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {logoUploading ? <Loader2 size={20} className="animate-spin mx-auto text-gray-400" /> : <><Upload size={18} className="mx-auto mb-1 text-gray-400" /> {t("settings.invoiceTemplateDropLogo")}</>}
                  </div>
                  {form.logoUrl && !form.useProfilePhotoAsLogo && (
                    <div className="flex items-center gap-3 p-2 rounded-xl bg-gray-50 dark:bg-white/[0.04]">
                      <img src={form.logoUrl} alt="" className="w-9 h-9 rounded-xl object-contain bg-white dark:bg-white/5 ring-1 ring-black/5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, logoUrl: "" }))} className="text-[13px] text-[#FF3B30] dark:text-[#FF453A] font-medium hover:underline">{t("settings.remove")}</button>
                    </div>
                  )}
                  <input type="url" value={form.useProfilePhotoAsLogo ? "" : form.logoUrl} onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value, useProfilePhotoAsLogo: false }))} placeholder="https://..." disabled={form.useProfilePhotoAsLogo} className="w-full h-10 px-3 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-[13px] text-gray-900 dark:text-white placeholder-gray-400 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF]/40" />
                </div>
              </div>
            </section>

            {/* 3. Scripts */}
            <section className="space-y-4 pt-6 border-t border-gray-100 dark:border-white/[0.06]">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white tracking-tight">{t("settings.invoiceTemplateScripts")}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateIntroText")}</label>
                  <textarea value={form.introText} onChange={(e) => setForm((f) => ({ ...f, introText: e.target.value }))} rows={2} placeholder={t("settings.invoiceTemplateIntroPlaceholder")} className="w-full px-3 py-2.5 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-[13px] text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF]/40" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateTermsText")}</label>
                  <textarea value={form.termsText} onChange={(e) => setForm((f) => ({ ...f, termsText: e.target.value }))} rows={2} placeholder={t("settings.invoiceTemplateTermsPlaceholder")} className="w-full px-3 py-2.5 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-[13px] text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF]/40" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{t("settings.invoiceTemplateFooter")}</label>
                  <textarea value={form.footerText} onChange={(e) => setForm((f) => ({ ...f, footerText: e.target.value }))} rows={2} placeholder={t("settings.invoiceTemplateFooterPlaceholder")} className="w-full px-3 py-2.5 rounded-xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-[13px] text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF]/40" />
                </div>
              </div>
            </section>

            {/* Enregistrer — intégré au flux */}
            <form onSubmit={handleSave} className="pt-6 border-t border-gray-100 dark:border-white/[0.06]">
              {success && (
                <div className="flex items-center gap-2 py-3 px-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-[13px] text-emerald-700 dark:text-emerald-400 font-medium mb-4">
                  <Check size={18} strokeWidth={2.2} /> {t("settings.saved")}
                </div>
              )}
              <button type="submit" disabled={saving || ownerProfileLoading} className="w-full h-12 flex items-center justify-center gap-2 rounded-2xl bg-[#007AFF] hover:bg-[#0066DD] active:scale-[0.99] text-white text-[15px] font-semibold shadow-[0_2px_8px_-2px_rgba(0,122,255,0.35)] hover:shadow-[0_4px_12px_-2px_rgba(0,122,255,0.4)] disabled:opacity-50 disabled:shadow-none transition-all duration-200">
                {saving ? <Loader2 size={20} className="animate-spin" strokeWidth={2} /> : <FileText size={20} strokeWidth={2} />}
                {saving ? t("settings.saving") : t("settings.saveBtn")}
              </button>
            </form>
          </div>
        </main>
      </div>
    </Modal>
  );
}
